// Theme runtime — three preferences: "light" (default, best-in-market like
// Claude/ChatGPT), "dark" (opt-in), and "system" (follow the OS
// prefers-color-scheme, live). The stored value is the *preference*; the
// resolved value ("light"/"dark") is written to `data-theme` on <html>, which
// the CSS token system keys off (see index.css — it honors data-theme, not
// prefers-color-scheme, so "system" must resolve to an explicit value here).
// Persisted to localStorage. Kept tiny and dependency-free so the resolve logic
// can also run as the inline no-FOUC bootstrap in public/index.html.
import { STORAGE_KEYS } from "@/config/storageKeys";

// NOTE: the inline no-FOUC bootstrap in public/index.html hardcodes this same
// key literal (it can't import) — keep the two in sync via STORAGE_KEYS.THEME.
const KEY = STORAGE_KEYS.THEME;
const PREFS = ["light", "dark", "system"];

// The stored *preference*: "light" | "dark" | "system". Anything else (legacy /
// absent) falls back to the light default — the documented product decision is
// "light unless the user asks otherwise".
export function getThemePref() {
  try {
    const v = localStorage.getItem(KEY);
    return PREFS.includes(v) ? v : "light";
  } catch {
    return "light";
  }
}

// True when the OS currently prefers a dark UI.
export function systemPrefersDark() {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

// Resolve a preference to the concrete theme that gets painted.
export function resolveTheme(pref) {
  if (pref === "system") return systemPrefersDark() ? "dark" : "light";
  return pref === "dark" ? "dark" : "light";
}

// Backward-compat: returns the currently *resolved* theme ("light"/"dark").
export function getTheme() {
  return resolveTheme(getThemePref());
}

// Paint a resolved theme onto <html> and keep the mobile browser chrome in sync.
function paint(resolved) {
  const t = resolved === "dark" ? "dark" : "light";
  document.documentElement.setAttribute("data-theme", t);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", t === "dark" ? "#11132a" : "#f6f7fc");
  return t;
}

// Apply a preference (resolving "system" against the OS). Returns the resolved
// theme actually painted.
export function applyTheme(pref) {
  return paint(resolveTheme(pref));
}

// Persist and apply a preference. Returns the stored preference.
export function setThemePref(pref) {
  const p = PREFS.includes(pref) ? pref : "light";
  applyTheme(p);
  try {
    localStorage.setItem(KEY, p);
  } catch {
    // private mode / storage blocked — theme still applies for this session.
  }
  return p;
}

// Backward-compat quick flip between light and dark (used by the command
// palette). Collapses "system" onto the opposite of what it currently resolves
// to, so the click always produces a visible change.
export function toggleTheme() {
  return setThemePref(getTheme() === "dark" ? "light" : "dark");
}

// Apply the stored (or default-light) preference. Call once at startup. When
// the preference is "system", live-track OS theme changes so the app follows
// the OS without a reload.
export function initTheme() {
  const resolved = applyTheme(getThemePref());
  try {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (getThemePref() === "system") applyTheme("system");
    };
    // Safari < 14 only has addListener; guard both.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
  } catch {
    // matchMedia unavailable — static resolve is fine.
  }
  return resolved;
}

// Setter kept for API compatibility (older callers passed a resolved theme).
export function setTheme(theme) {
  return setThemePref(theme === "dark" ? "dark" : "light");
}
